/* Rugged Unleashed — Performance Stack builder.
   Open quantity: no cap. Each product is added as a real Shopify line item so
   inventory deducts natively. Discounts are applied by Shopify's automatic
   discounts at checkout; this file only mirrors the maths for display. */

class PerformanceStack extends HTMLElement {
  connectedCallback() {
    const cfg = this.querySelector('[data-ps-config]');
    if (!cfg) return;
    try {
      this.cfg = JSON.parse(cfg.textContent);
    } catch (e) {
      // A stray trailing comma in the template's config must never take the whole
      // builder (and the add-to-cart) down with it. Repair and retry once.
      this.cfg = JSON.parse(cfg.textContent.replace(/,\s*([\]}])/g, '$1'));
    }

    /* dataset values are strings, config ids are numbers — normalise once so
       every strict comparison below actually matches. */
    this.products = this.cfg.products
      .filter((p) => p.available)
      .map((p) => Object.assign({}, p, { id: String(p.id) }));
    if (!this.products.length) return;

    /* highest applicable tier wins, so order does not matter in the theme editor */
    this.tiers = (this.cfg.tiers || []).slice().sort((a, b) => a.min - b.min);

    this.qty = {};
    this.products.forEach((p) => { this.qty[p.id] = 0; });
    (this.cfg.defaults || []).forEach((d) => {
      const p = this.products.find((x) => x.id === String(d.id));
      if (p) this.qty[p.id] = d.qty;
    });

    this.els = {
      ladder: this.querySelector('[data-ps-ladder]'),
      products: this.querySelector('[data-ps-products]'),
      progress: this.querySelector('[data-ps-progress]'),
      shipWrap: this.querySelector('[data-ps-ship]'),
      shipMsg: this.querySelector('[data-ps-ship-msg]'),
      shipBar: this.querySelector('[data-ps-ship-bar]'),
      regular: this.querySelector('[data-ps-regular]'),
      saveRow: this.querySelector('[data-ps-save-row]'),
      saveLbl: this.querySelector('[data-ps-save-label]'),
      save: this.querySelector('[data-ps-save]'),
      total: this.querySelector('[data-ps-total]'),
      atc: this.querySelector('[data-ps-atc]'),
      error: this.querySelector('[data-ps-error]'),
      live: this.querySelector('[data-ps-live]'),
      sticky: this.querySelector('[data-ps-sticky]'),
      stickyTotal: this.querySelector('[data-ps-sticky-total]'),
      stickyCount: this.querySelector('[data-ps-sticky-count]'),
      stickyAtc: this.querySelector('[data-ps-sticky-atc]')
    };

    this.bind();
    this.render();
    this.observeSticky();
  }

  count() { return this.products.reduce((s, p) => s + (this.qty[p.id] || 0), 0); }

  tierFor(n) {
    let best = null;
    this.tiers.forEach((t) => { if (n >= t.min) best = t; });
    return best;
  }

  bind() {
    this.els.products.addEventListener('click', (e) => {
      const b = e.target.closest('[data-ps-step]');
      if (!b) return;
      const id = String(b.dataset.psProduct);
      if (b.dataset.psStep === 'inc') this.qty[id]++;
      else if (this.qty[id] > 0) this.qty[id]--;
      this.render();
    });
    [this.els.atc, this.els.stickyAtc].forEach((b) => {
      if (b) b.addEventListener('click', () => this.addToCart());
    });
  }

  money(cents) {
    const fmt = this.cfg.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
               withCommas.replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toLocaleString());
  }

  render() {
    const n = this.count();
    const tier = this.tierFor(n);
    const pct = tier ? tier.discount : 0;

    const regular = this.products.reduce((s, p) => s + this.qty[p.id] * p.price, 0);
    // Shopify rounds a percentage discount DOWN to the cent (51.98 at 5% is -2.59,
    // not -2.60). Mirror that exactly so the builder never shows a price a cent
    // lower than checkout charges. Integer cents x integer pct stays exact.
    const discount = Math.floor((regular * pct) / 100);
    const net = regular - discount;

    /* ladder */
    if (this.els.ladder) {
      Array.from(this.els.ladder.querySelectorAll('[data-ps-rung]')).forEach((r) => {
        const min = parseInt(r.dataset.psRung, 10);
        r.classList.toggle('is-active', tier ? min === tier.min : false);
      });
    }

    /* product cards */
    this.products.forEach((p) => {
      const v = this.querySelector(`[data-ps-value="${p.id}"]`);
      if (v) v.textContent = this.qty[p.id];
      const dec = this.querySelector(`[data-ps-step="dec"][data-ps-product="${p.id}"]`);
      if (dec) dec.disabled = this.qty[p.id] === 0;
      const card = this.querySelector(`[data-ps-card="${p.id}"]`);
      if (card) card.classList.toggle('is-picked', this.qty[p.id] > 0);
    });

    /* free shipping — measured against the real discounted total, never the item
       count, so it can never promise something checkout will not honour */
    const threshold = this.cfg.freeShippingThreshold;
    const unlocked = threshold > 0 && net >= threshold;
    if (this.els.shipWrap && threshold > 0) {
      const remaining = Math.max(0, threshold - net);
      const progress = Math.max(0, Math.min(100, (net / threshold) * 100));
      this.els.shipWrap.classList.toggle('is-unlocked', unlocked);
      this.els.shipMsg.innerHTML = unlocked
        ? this.cfg.text.shipUnlocked
        : this.cfg.text.shipProgress.replace('[amount]', `<b>${this.money(remaining)}</b>`);
      this.els.shipBar.style.width = progress.toFixed(1) + '%';
    }

    /* Discount progress — states the next threshold in plain terms rather than
       leaving the customer to work the ladder out for themselves. */
    if (this.els.progress) {
      const next = this.tiers.filter((t) => t.min > n).sort((a, b) => a.min - b.min)[0];
      let msg = '';
      if (n === 0) {
        msg = this.cfg.text.progressEmpty || '';
      } else if (next) {
        const need = next.min - n;
        msg = (tier ? this.cfg.text.progressUnlockedNext : this.cfg.text.progressNext)
          .replace('[need]', need)
          .replace('[items]', need === 1 ? this.cfg.text.item : this.cfg.text.items)
          .replace('[next]', next.discount)
          .replace('[pct]', pct);
      } else if (tier) {
        msg = this.cfg.text.progressMax.replace('[pct]', pct);
      }
      this.els.progress.innerHTML = msg;
      this.els.progress.hidden = msg === '';
    }

    /* summary */
    this.els.regular.textContent = this.money(regular);
    this.els.save.textContent = '-' + this.money(discount);
    if (this.els.saveLbl) {
      this.els.saveLbl.textContent = this.cfg.text.savingsLabel.replace('[pct]', pct);
    }
    this.els.saveRow.hidden = discount === 0;
    this.els.total.textContent = this.money(net);

    const empty = n === 0;
    /* The label may carry a '|' to mark a deliberate line break. Shopify strips
       real newlines out of template JSON, so a delimiter is the only way to let
       the merchant control where the button wraps. The price is not repeated
       here — the summary directly above already states it, and the sticky bar
       carries its own total. */
    const label = empty ? this.cfg.text.ctaEmpty : this.cfg.text.cta;
    const parts = String(label).split('|').map((t) => t.trim()).filter(Boolean);
    [this.els.atc, this.els.stickyAtc].forEach((b) => {
      if (!b) return;
      b.disabled = empty;
      if (b === this.els.stickyAtc) {
        b.textContent = parts.join(' ');       /* the bar is short; keep one line */
      } else {
        b.textContent = '';
        parts.forEach((t, i) => {
          if (i) b.appendChild(document.createElement('br'));
          b.appendChild(document.createTextNode(t));
        });
      }
    });

    if (this.els.stickyTotal) this.els.stickyTotal.textContent = this.money(net);
    if (this.els.stickyCount) {
      this.els.stickyCount.textContent =
        n + ' ' + (n === 1 ? this.cfg.text.item : this.cfg.text.items);
    }
  }

  observeSticky() {
    if (!this.els.sticky || !('IntersectionObserver' in window)) return;
    const anchor = this.els.atc;
    if (!anchor) return;
    new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        this.els.sticky.classList.toggle(
          'is-visible', !e.isIntersecting && e.boundingClientRect.top < 0 && this.count() > 0);
      });
    }, { threshold: 0 }).observe(anchor);
  }

  async addToCart() {
    if (this.count() === 0) return;
    const items = this.products
      .filter((p) => this.qty[p.id] > 0)
      .map((p) => ({ id: p.variantId, quantity: this.qty[p.id] }));
    if (!items.length) return;

    this.els.error.hidden = true;
    [this.els.atc, this.els.stickyAtc].forEach((b) => b && b.classList.add('is-loading'));

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = new FormData();
    items.forEach((it, i) => {
      body.append(`items[${i}][id]`, it.id);
      body.append(`items[${i}][quantity]`, it.quantity);
    });
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = body;

    try {
      const res = await fetch(`${routes.cart_add_url}`, config);
      const data = await res.json();
      if (data.status) {
        this.els.error.textContent = data.description || data.message;
        this.els.error.hidden = false;
        return;
      }
      if (!cart) { window.location = window.routes.cart_url; return; }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'performance-stack' });
      cart.classList.remove('is-empty');
      cart.renderContents(data);
    } catch (e) {
      this.els.error.textContent = this.cfg.text.error;
      this.els.error.hidden = false;
    } finally {
      [this.els.atc, this.els.stickyAtc].forEach((b) => b && b.classList.remove('is-loading'));
    }
  }
}

customElements.get('performance-stack') || customElements.define('performance-stack', PerformanceStack);
